import React, { useEffect, useRef, useState, useCallback } from 'react'
import PageHeader from '../utils/PageHeader'
import { API_URL } from '../utils/Constants/URLS'
import PageLoader from '../utils/PageLoader'

// Lazy-loading OrderBook using IntersectionObserver and PageLoader overlay
export default function OrderBook() {
  const token = localStorage.getItem('token')
  const _now = new Date()
  const _year = _now.getFullYear()
  const _month = String(_now.getMonth() + 1).padStart(2, '0')
  const defaultStart = `${_year}-${_month}-01`
  const defaultEnd = _now.toISOString().slice(0, 10) // YYYY-MM-DD

  // data is now incremental (pages)
  const [data, setData] = useState([])
  const [startDate, setStartDate] = useState(defaultStart)
  const [endDate, setEndDate] = useState(defaultEnd)
  const [loading, setLoading] = useState(false) // for page-level fetch
  const [pageLoading, setPageLoading] = useState(false) // for "load-more" small fetches
  const [error, setError] = useState(null)

  // lazy/pagination
  const [page, setPage] = useState(1)
  const limit = 50 // items per page (tune as needed)
  const [hasMore, setHasMore] = useState(true)

  const observerRef = useRef(null)

  // Build API URL for page & date range
  const buildUrl = (p = page) => {
    const s = encodeURIComponent(startDate)
    const e = encodeURIComponent(endDate)
    return `${API_URL}leads/sales-orders/report?pagination=false&startDate=${s}&endDate=${e}`
  }

  // Fetch a specific page (used for initial load and subsequent lazy loads)
  const fetchPage = useCallback(
    async (p = 1, replace = false) => {
      // when p === 1 and replace === true -> full reload
      try {
        if (p === 1 && replace) setLoading(true)
        else setPageLoading(true)
        setError(null)

        const res = await fetch(buildUrl(p), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!res.ok) throw new Error(`Server responded with ${res.status}`)
        const result = await res.json()

        // API shape when pagination=true assumed: { error:false, data: { docs: [], totalDocs, totalPages } }
        const docs = result?.data?.docs || result?.data?.reportData || []

        setData(prev => (p === 1 && replace ? docs : [...prev, ...docs]))

        // if docs length < limit -> no more data
        if (docs.length < limit) setHasMore(false)
        else setHasMore(true)
      } catch (err) {
        setError(err.message || 'Failed to fetch')
      } finally {
        setLoading(false)
        setPageLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, startDate, endDate]
  )

  // initial load (or when date range changes)
  useEffect(() => {
    setPage(1)
    fetchPage(1, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, startDate, endDate])

  // IntersectionObserver for sentinel -> load next page
  useEffect(() => {
    if (!hasMore) return
    const sentinel = observerRef.current
    if (!sentinel) return

    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !pageLoading && !loading) {
            setPage(prev => prev + 1)
          }
        })
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [hasMore, pageLoading, loading])

  // when page changes (and page > 1) fetch that page
  useEffect(() => {
    if (page === 1) return
    fetchPage(page)
  }, [page, fetchPage])

  const handleFetchClick = () => {
    if (startDate > endDate) {
      setError('Start date cannot be after end date')
      return
    }
    // reload from page 1 with replace
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  const handleReset = () => {
    setStartDate(defaultStart)
    setEndDate(defaultEnd)
    setError(null)
    setPage(1)
    fetchPage(1, true)
  }

  // CSV helpers
  // quote a single field (escape internal double-quotes by doubling them)
  const quote = value => {
    if (value === null || value === undefined) return '""'
    const str = String(value)
    return `"${str.replace(/"/g, '""')}"`
  }

  // the header labels used in CSV (same set/order as table)
  const csvHeaderLabels = [
    'Area',
    'Sales Executive',
    'Business Line',
    'Type of industry',
    'Lead ID',
    'SO ID',
    'Date of SO',
    'Age',
    'Customer Name',
    'Customer PO Number',
    'Customer PO Date',
    'Zero Date Delivery',
    'Product Line',
    'Product Name',
    'Part Code',
    'Quantity',
    'SO Value',
    'Invoiced Qty',
    'Invoiced Value',
    'Pending Qty',
    'Pending Value',
    'Committed Delivery Date',
    'Price Basis',
    'Responsibility of transportation',
    'Type of Transaction',
    'Bank Guarantee Details',
    'New/ Existing Customer',
    'Type of Sale (Direct/ GSPL)'
  ]

  // build CSV and trigger download (quotes every field)
  const downloadCsv = () => {
    if (!data || data.length === 0) {
      setError('No data to download for the selected range.')
      return
    }
    setError(null)

    // header row (quote headers too)
    const headerRow = csvHeaderLabels.map(h => quote(h)).join(',')

    // build rows in same order as headers
    const csvLines = data.map(dt => {
      const rowObj = {
        Area: Array.isArray(dt?.area) ? dt.area.join(', ') : dt?.area ?? '',
        'Sales Executive': dt?.salesExecutive ?? '',
        'Business Line': dt?.businessLine ?? '',
        'Type of industry': dt?.typeOfIndustry ?? '',
        'Lead ID': dt?.leadId ?? '',
        'SO ID': dt?.soNumber ?? '',
        'Date of SO': dt?.dateOfSO
          ? new Date(dt.dateOfSO).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
          : '',
        'Age': dt?.age,
        'Customer Name': dt?.customerName ?? '',
        'Customer PO Number': dt?.customerPONumber ?? '',
        'Customer PO Date': dt?.customerPODate
          ? new Date(dt.customerPODate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
          : '',
        'Zero Date Delivery': dt?.zeroDateOfDelivery
          ? new Date(dt.zeroDateOfDelivery).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
          : '',
        'Product Line': dt?.productLine ?? '',
        'Product Name': dt?.productName ?? '',
        'Part Code': dt?.partCode ?? '',
        Quantity: dt?.quantity ?? '',
        'SO Value': dt?.soValue ?? '',
        'Invoiced Qty': dt?.invoicedQuantity ?? '',
        'Invoiced Value': dt?.invoicedQuantityValue ?? '',
        "Pending Qty": dt?.pendingQuantity ?? '',
        "Pending Value": dt?.pendingQuantityValue ?? '',
        'Committed Delivery Date': dt?.committedDeliveryDate
          ? new Date(dt.committedDeliveryDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
          : '',
        'Price Basis': dt?.priceBasis ?? '',
        'Responsibility of transportation':
          dt?.responsibilityOfTransportation ?? '',
        'Type of Transaction': dt?.typeOfTransaction ?? '',
        'Bank Guarantee Details': Array.isArray(dt?.bankGuaranteeDetails)
          ? dt.bankGuaranteeDetails.join(', ')
          : dt?.bankGuaranteeDetails ?? '',
        'New/ Existing Customer': dt?.customerType ?? '',
        'Type of Sale (Direct/ GSPL)': dt?.typeOfSale ?? ''
      }

      const values = csvHeaderLabels.map(h => quote(rowObj[h]))
      return values.join(',')
    })

    const csvContent = [headerRow, ...csvLines].join('\r\n')

    // create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `order_book_${startDate}_to_${endDate}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <PageHeader
        pageName='ORDER BOOKING DETAILS'
        breadcrump1='Reports'
        breadcrump2='Order Booking'
        link='/order-booking'
      />

      <div className='p-6 min-h-screen'>
        {/* date selectors */}
        <div className='max-w-[1400px] mx-2 mb-4 bg-white border border-gray-200 p-4 rounded-md'>
          <div className='flex flex-col md:flex-row md:items-end md:justify-between gap-3'>
            <div className='flex flex-col md:flex-row md:items-end gap-3'>
              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>Start Date</label>
                <input
                  type='date'
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  max={endDate}
                />
              </div>

              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>End Date</label>
                <input
                  type='date'
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className='px-2 py-1 border rounded'
                  min={startDate}
                />
              </div>

              <div className='flex items-center gap-2 mt-2 md:mt-0'>
                <button
                  onClick={handleFetchClick}
                  className='px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700'
                >
                  Fetch
                </button>
                <button
                  onClick={handleReset}
                  className='px-4 py-2 border rounded hover:bg-gray-50'
                >
                  Reset
                </button>
              </div>
            </div>

            <div className='flex items-center justify-end'>
              <button
                onClick={downloadCsv}
                className='px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700'
              >
                Download
              </button>
            </div>
          </div>

          {error && <div className='mt-3 text-sm text-red-600'>{error}</div>}
        </div>

        <div className='overflow-x-auto bg-white border border-gray-200 max-w-[1400px] mx-2 relative'>
          {/* PageLoader overlay when performing a large reload */}
          {loading && (
            <div className='absolute inset-0 z-50 flex items-center justify-center bg-white/60'>
              <PageLoader />
            </div>
          )}

          <table className='min-w-full border-collapse table-auto'>
            <thead className='bg-gradient-to-r from-indigo-500 to-indigo-600  text-white text-sm font-semibold uppercase tracking-wide'>
              <tr>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Area
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Sales Executive
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Business Line
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Type of industry
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Lead ID
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  SO ID
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Date of SO
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Age
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Customer Name
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Customer PO Number
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Customer PO Date
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Zero Date Delivery
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Product Line
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Product Name
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Part Code
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Quantity
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  SO Value
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Invoiced Qty
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Invoiced Value
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Pending Qty
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Pending Value
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Committed Delivery Date
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Price Basis
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Responsibility of transportation
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Type of Transaction
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Bank Guarantee Details
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  New/ Existing Customer
                </th>
                <th className='px-3 py-2 border-b border-gray-300 text-left whitespace-nowrap'>
                  Type of Sale (Direct/ GSPL)
                </th>
              </tr>
            </thead>
            <tbody className='text-sm text-gray-800'>
              {data.map((dt, index) => (
                <tr key={`${dt?.soNumber ?? 'row'}-${index}`}>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {Array.isArray(dt?.area) ? dt.area.join(', ') : dt?.area}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.salesExecutive}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.businessLine}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.typeOfIndustry}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.leadId}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.soNumber}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.dateOfSO
                      ? new Date(dt.dateOfSO).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })
                      : ''}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.age}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.customerName}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.customerPONumber}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.customerPODate
                      ? new Date(dt.customerPODate).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }
                      )
                      : ''}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.zeroDateOfDelivery
                      ? new Date(dt.zeroDateOfDelivery).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }
                      )
                      : ''}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.productLine}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.productName}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.partCode}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.quantity}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.soValue}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.invoicedQuantity}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.invoicedQuantityValue}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.pendingQuantity}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.pendingQuantityValue}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.committedDeliveryDate
                      ? new Date(dt.committedDeliveryDate).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        }
                      )
                      : ''}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.priceBasis}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.responsibilityOfTransportation}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.typeOfTransaction}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {Array.isArray(dt?.bankGuaranteeDetails)
                      ? dt.bankGuaranteeDetails.join(', ')
                      : dt?.bankGuaranteeDetails}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.customerType}
                  </td>
                  <td className='px-4 py-3 border-b border-gray-200 whitespace-nowrap'>
                    {dt?.typeOfSale}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* sentinel for intersection observer lazy-load */}
          <div
            ref={observerRef}
            className='h-8 flex items-center justify-center'
          >
            {pageLoading && <PageLoader />}
            {!hasMore && !loading && (
              <div className='p-4 text-center text-sm text-gray-500'>
                No more records.
              </div>
            )}
          </div>

          {!loading && data.length === 0 && !error && (
            <div className='p-6 text-center text-sm text-gray-600'>
              No records found for selected date range.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
